import { useState } from 'preact/hooks';
import {
  ApiError,
  authorityOfferFrom,
  bindMembershipParty,
  createRecord,
  inviteMember,
  recordMemberDateOfBirth,
  type PackJobRole,
} from '../lib/api';
import { AUTHORITY_GATE_LEAD, AUTHORITY_NO_ROAD } from '../lib/authorityGate';
import { bumpRecordsVersion } from '../lib/recordEditor';
import './TeamDoor.css';

// S12b — THE DOOR THAT WAS MISSING.
//
// POST /auth/invites has been live and proven since C6b; nothing in this
// app had ever called it. So an owner — holding every authority the server
// recognises — could not invite a single person, from any screen. This is
// that door, and it is deliberately the ONLY new surface: no rail
// destination, no Team screen (blessed nav, wf02 §01b, has no Team tab and
// S12b is not the chunk that changes it). It opens on Techs, the people
// surface the product already has.
//
// TWO PANELS, because Huan asked two different questions on the same day:
//
//   InvitePanel  — "I want to hit the invite button." An email, a job role
//                  the PACK declares, and a receipt naming who and as what.
//
//   AddTechPanel — "When I try and add a tech, does that actually mint the
//                  new account and send them an invite email?" It did not:
//                  "+ New technician" wrote an hvac_tech_profile RECORD and
//                  nothing else, and nothing on the screen said so. Now the
//                  same step can do the whole thing — record, membership,
//                  invite email, and C5f's binding between the person and
//                  the record that IS them, which is what makes the Rexx
//                  manifest's "@assigned" capabilities reach a new tech at
//                  all.
//
// THE HONESTY IS IN THE BUTTON, NOT IN A CAPTION. The submit reads "Add to
// roster" until there is an email to invite, and "Add and invite" the
// moment there is one. A screen that has to explain itself in small print
// is a screen that lies by default and apologises in a footnote.
//
// AUTHORITY IS THE SERVER'S. Neither panel asks who you are before letting
// you press: the route's own gate answers, and its refusal is rendered
// verbatim. A client-side role check deciding whether the door is DRAWN is
// how Huan came to hold owner and see no option anywhere.

/** What just happened, in the words the receipt will use. */
export interface DoorReceipt {
  /** The headline — who, and what became of them. */
  line: string;
  /** The mail half, told separately because it is the half that can fail
   *  on its own. Null when nothing was ever meant to be sent. */
  mail: string | null;
  /** True when some of the work landed and some did not — the receipt goes
   *  out in the warning voice rather than the calm one. */
  partial?: boolean;
}

/** A role KEY as a person reads it. The keys are the pack's own slugs and
 * some are compound — actor_assist seats `co_parent` — so an underscore is a
 * hyphen here or a household is offered "Co_parent" as a job title. */
function titleCase(s: string): string {
  const human = s.replace(/_/g, '-');
  return human.length === 0 ? human : human[0]!.toUpperCase() + human.slice(1);
}

function messageOf(err: unknown): string {
  // ⚑ THE GATE'S 409 IS NOT AN ERROR REPORT (2026-09-16, the Day 3–4 walk,
  // item 6). Since the invite route asks the pack's authority manifest, a
  // member who does not hold the act gets `{authority:'convert', …}` — a body
  // with no `error` envelope, which ApiError's normalizer renders as the
  // literal "request failed". That is the exact silence lib/authorityGate.ts
  // was written to end, so this door speaks its vocabulary rather than a
  // second one. An invite cannot be replayed by the cascade executor, so the
  // gate offers no ask (`fileAsk: { available: false }`) and the honest half
  // of the sentence is the no-road half.
  const offer = authorityOfferFrom(err);
  if (offer && offer.authority === 'convert') {
    return `${AUTHORITY_GATE_LEAD} ${AUTHORITY_NO_ROAD}`;
  }
  return err instanceof ApiError ? err.message : 'Could not reach the server.';
}

/** The pack's own job-role vocabulary, as pressable pills. Never a
 *  hardcoded owner/admin/dispatcher/tech list — `packJobRoles` comes from
 *  the manifest's `seating`, the same declaration POST /auth/invites reads
 *  to decide which tier the invite seats them at. */
function JobRolePicker({
  roles,
  value,
  onChange,
}: {
  roles: PackJobRole[];
  value: string | null;
  onChange: (next: string) => void;
}) {
  return (
    <div class="team-door__roles" role="radiogroup" aria-label="Job role">
      {roles.map((r) => (
        <button
          type="button"
          key={r.key}
          role="radio"
          aria-checked={value === r.key}
          class={value === r.key ? 'team-door__role team-door__role--on' : 'team-door__role'}
          onClick={() => onChange(r.key)}
        >
          {titleCase(r.key)}
        </button>
      ))}
    </div>
  );
}

function Receipt({ receipt }: { receipt: DoorReceipt }) {
  return (
    <div
      class={receipt.partial ? 'team-door__receipt team-door__receipt--partial' : 'team-door__receipt'}
      role="status"
      data-testid="team-door-receipt"
    >
      <span class="team-door__receipt-stripe" aria-hidden="true" />
      <span>
        <b>{receipt.line}</b>
        {receipt.mail !== null ? <small>{receipt.mail}</small> : null}
      </span>
    </div>
  );
}

/** One seat this pack offers to invite into, in the pack's own words — the
 *  `entry.settings.invite.seats` declaration, passed straight through. The
 *  words are never composed here: a household's second adult and its helper
 *  are the pack's nouns, and a shell that invented either would be a second
 *  copy of the pack's vocabulary waiting to drift. */
export interface InviteSeat {
  jobRole: string;
  label: string;
  sub?: string;
}

export interface InvitePanelProps {
  tenantId: string;
  /** The pack's declared job roles. An empty list means the active pack
   *  declares none — the panel says so instead of inventing four. */
  roles: PackJobRole[];
  /** G-18 — THE ONE SEAT THIS PANEL WAS OPENED FOR, when the caller picked
   *  it from the pack's declared list. The role picker is then absent: the
   *  question was already answered on the row that opened this, and asking
   *  it twice is how a person ends up inviting a helper "as Co-parent".
   *
   *  Null/absent keeps the original panel exactly as it was — an address and
   *  the pack's own pills — which is what every Rexx surface still opens. */
  seat?: InviteSeat | null;
  /** G-17 — THIS PACK REFUSES TO ACTIVATE A SEAT WITH NO BIRTH DATE ON FILE
   *  (`requiresMemberAge`, read from GET /api/team/me).
   *
   *  ⚑ THIS IS NOT A NICETY, IT IS THE DIFFERENCE BETWEEN AN INVITATION AND
   *  A DEAD LETTER. `POST /auth/invites/accept` runs the pack's beforeActivate
   *  guard, which refuses every non-founder whose date of birth is not
   *  recorded — 409 `member_age_required`, "Ask the account owner to add it".
   *  The only screen that recorded one was the add-a-performer sheet, which
   *  only ever invites a child, so an invited co-parent was unactivatable
   *  from the product and the owner had nothing to act on.
   *
   *  Absent on a pack without the rule, and then no field is drawn: asking
   *  a Rexx technician for their birth date would be collecting a datum
   *  nothing in that deployment is allowed to use. */
  requiresBirthDate?: boolean;
  /** Fired after a successful invite so the caller can reload the roster
   *  and show the new pending line. */
  onInvited: () => void;
  onClose: () => void;
}

export function InvitePanel({
  tenantId,
  roles,
  seat = null,
  requiresBirthDate = false,
  onInvited,
  onClose,
}: InvitePanelProps) {
  const [email, setEmail] = useState('');
  const [pickedRole, setPickedRole] = useState<string | null>(null);
  const [dob, setDob] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<DoorReceipt | null>(null);

  // The seat the caller named wins; otherwise it is whichever pill was
  // pressed. One value either way — a panel with two notions of "which
  // seat" is a panel that will one day send the other one.
  const jobRole = seat ? seat.jobRole : pickedRole;
  /** The seat as the RECEIPT should name it: the pack's own label when there
   * is one, the role key made readable when there is not. */
  const seatWords = seat ? seat.label : jobRole ? titleCase(jobRole) : '';

  const ready =
    email.trim().length > 0 && jobRole !== null && (!requiresBirthDate || dob.trim().length > 0) && !busy;

  async function send() {
    if (!ready || jobRole === null) return;
    const address = email.trim();
    setBusy(true);
    setError(null);
    try {
      // ---- 1. the seat ---------------------------------------------
      const invited = await inviteMember(tenantId, address, [jobRole]);

      // ---- 2. the birth date, in the SAME act ----------------------
      // AddPerformer's sequence, for the same reason and in the same order:
      // without this the seat can never go active, so it is part of
      // inviting rather than a follow-up somebody might not make. It is a
      // SEPARATE CALL because there is no route that takes both, and that
      // is exactly why the half-state below has to be said out loud.
      if (requiresBirthDate) {
        try {
          await recordMemberDateOfBirth(tenantId, invited.membershipId, dob.trim());
        } catch (err) {
          // ⚑ A PARTIAL FAILURE MUST NOT LIE. The invitation exists and the
          // email has gone; the date has not landed, so the person who opens
          // that link will be refused by the age guard. Saying "invited" here
          // would set up a refusal nobody could explain — and the invite's id
          // is named because it is the handle the owner holds.
          setReceipt({
            line: `${seatWords ? `${seatWords} — ` : ''}${address} is invited, and their date of birth was refused.`,
            mail:
              `Their seat cannot go live until it is recorded. The invitation is ` +
              `${invited.membershipId}. ${messageOf(err)}`,
            partial: true,
          });
          setEmail('');
          setDob('');
          setPickedRole(null);
          onInvited();
          return;
        }
      }

      // The route awaits the mailer before it answers, so a 201 is the
      // mail's receipt too — it cannot have returned one without the
      // other. That is why this line may say the email went.
      setReceipt({
        line: seat
          ? `${seat.label} — ${address} is invited.`
          : `${address} is invited as ${titleCase(jobRole)}.`,
        mail:
          'The invite email has been sent. The link works once, and expires in seven days.' +
          (requiresBirthDate ? ' Their date of birth is on file, so the seat goes live when they accept.' : ''),
      });
      setEmail('');
      setDob('');
      setPickedRole(null);
      onInvited();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="team-door" data-testid="invite-panel">
      <div class="team-door__head">
        {/* THE SEAT IS THE HEADING when the caller named one. "Invite
            someone" above a panel that can only invite a helper is a title
            that describes the mechanism instead of the act. */}
        <h3 class="team-door__title">{seat ? seat.label : 'Invite someone'}</h3>
        <button type="button" class="team-door__close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>

      {/* The pack's own line about what this seat sees, where it is relevant
          — carried from the declaration, never composed here. */}
      {seat?.sub ? <p class="team-door__seat-sub">{seat.sub}</p> : null}

      {receipt ? <Receipt receipt={receipt} /> : null}

      <label class="team-door__field">
        <span class="team-door__label">Email</span>
        <input
          class="team-door__input"
          type="email"
          value={email}
          autocomplete="off"
          autocorrect="off"
          spellcheck={false}
          data-testid="invite-email"
          onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
        />
      </label>

      {/* G-17 — THE DATE, WITH THE REASON ATTACHED TO THE ASK. Not a caption
          teaching the screen: the field's own answer to "why are you asking
          me this", said once, where it is asked. Drawn only on a pack whose
          seats genuinely refuse to go live without it. */}
      {requiresBirthDate ? (
        <label class="team-door__field">
          <span class="team-door__label">Their date of birth</span>
          <input
            class="team-door__input"
            type="date"
            value={dob}
            data-testid="invite-dob"
            onInput={(e) => setDob((e.currentTarget as HTMLInputElement).value)}
          />
          <span class="team-door__aside">
            A seat here goes live only once one is on file. It is also what the rules protecting
            a young performer are read from.
          </span>
        </label>
      ) : null}

      {seat ? null : roles.length > 0 ? (
        <JobRolePicker roles={roles} value={pickedRole} onChange={setPickedRole} />
      ) : (
        <div class="team-door__error">This build's pack declares no job roles to invite into.</div>
      )}

      {error !== null ? (
        <div class="team-door__error" role="alert" data-testid="invite-error">
          {error}
        </div>
      ) : null}

      <button
        type="button"
        class="team-door__go"
        disabled={!ready}
        data-testid="invite-send"
        onClick={() => void send()}
      >
        {busy ? 'Sending…' : 'Send invite'}
      </button>
    </div>
  );
}

export interface AddTechPanelProps {
  tenantId: string;
  /** The pack's job roles, used only to confirm this pack HAS a 'tech'
   *  role before offering to invite into it. */
  roles: PackJobRole[];
  onDone: () => void;
  onClose: () => void;
}

const TECH_TYPE = 'hvac_tech_profile';
const TECH_JOB_ROLE = 'tech';

export function AddTechPanel({ tenantId, roles, onDone, onClose }: AddTechPanelProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [alsoInvite, setAlsoInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<DoorReceipt | null>(null);

  const canInvite = roles.some((r) => r.key === TECH_JOB_ROLE);
  const hasEmail = email.trim().length > 0;
  // The whole of Part B's honesty, in one boolean: an invite happens when,
  // and only when, there is somewhere to send it.
  const willInvite = canInvite && hasEmail && alsoInvite;
  const ready = name.trim().length > 0 && !busy;

  async function submit() {
    if (!ready) return;
    const who = name.trim();
    const address = email.trim();
    setBusy(true);
    setError(null);
    try {
      const fields: Record<string, unknown> = { name: who };
      if (address.length > 0) fields['email'] = address;
      if (phone.trim().length > 0) fields['phone'] = phone.trim();
      const record = await createRecord(tenantId, TECH_TYPE, fields);
      bumpRecordsVersion();

      if (!willInvite) {
        // Said plainly, and said by the outcome rather than by a caption:
        // a roster line is a record, not a person who can sign in.
        setReceipt({
          line: `${who} is on the roster.`,
          mail: address.length > 0 ? 'No invite sent — nobody was emailed.' : 'No sign-in yet, and nobody was emailed.',
        });
        setName('');
        setEmail('');
        setPhone('');
        onDone();
        return;
      }

      // From here the record already exists, so every failure below is
      // PARTIAL and says which half landed. Silence would leave a roster
      // line that looks like an account and is not one.
      let membershipId: string;
      try {
        const invited = await inviteMember(tenantId, address, [TECH_JOB_ROLE]);
        membershipId = invited.membershipId;
      } catch (err) {
        setReceipt({
          line: `${who} is on the roster, but the invite did not send.`,
          mail: messageOf(err),
          partial: true,
        });
        onDone();
        return;
      }

      // C5f: bind the person to the record that IS them. Failing here does
      // not undo the invite — they can still sign in; what they lose is the
      // manifest's "@assigned" reach until an admin binds them.
      try {
        await bindMembershipParty(tenantId, membershipId, record.id);
      } catch (err) {
        setReceipt({
          line: `${who} is on the roster and invited as Tech.`,
          mail: `The invite email has been sent. Their profile could not be linked to the invite: ${messageOf(err)}`,
          partial: true,
        });
        onDone();
        return;
      }

      // S5d §3 — when a phone rode along with an invite, say what becomes of
      // it: NOTHING is enrolled at add time (the membership is not active
      // yet — the server refuses, correctly), so the truthful sentence is
      // where the door will be once they accept. Enrolling automatically on
      // acceptance is server-side machinery this panel does not have.
      setReceipt({
        line: `${who} is on the roster and invited as Tech.`,
        mail:
          `The invite email has been sent to ${address}. The link works once, and expires in seven days.` +
          (phone.trim().length > 0
            ? ' Once they accept, their Telegram connects from their roster line.'
            : ''),
      });
      setName('');
      setEmail('');
      setPhone('');
      onDone();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="team-door" data-testid="add-tech-panel">
      <div class="team-door__head">
        <h3 class="team-door__title">New technician</h3>
        <button type="button" class="team-door__close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>

      {receipt ? <Receipt receipt={receipt} /> : null}

      <label class="team-door__field">
        <span class="team-door__label">Name</span>
        <input
          class="team-door__input"
          type="text"
          value={name}
          data-testid="add-tech-name"
          onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
        />
      </label>

      <label class="team-door__field">
        <span class="team-door__label">Email</span>
        <input
          class="team-door__input"
          type="email"
          value={email}
          autocomplete="off"
          autocorrect="off"
          spellcheck={false}
          data-testid="add-tech-email"
          onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
        />
      </label>

      <label class="team-door__field">
        <span class="team-door__label">Phone</span>
        <input
          class="team-door__input"
          type="tel"
          value={phone}
          autocomplete="off"
          data-testid="add-tech-phone"
          onInput={(e) => setPhone((e.currentTarget as HTMLInputElement).value)}
        />
      </label>

      {canInvite && hasEmail ? (
        <button
          type="button"
          role="switch"
          aria-checked={alsoInvite}
          class={alsoInvite ? 'team-door__toggle team-door__toggle--on' : 'team-door__toggle'}
          data-testid="add-tech-invite-toggle"
          onClick={() => setAlsoInvite((v) => !v)}
        >
          <span class="team-door__toggle-box" aria-hidden="true">
            {alsoInvite ? '✓' : ''}
          </span>
          Invite them as Tech
        </button>
      ) : null}

      {error !== null ? (
        <div class="team-door__error" role="alert" data-testid="add-tech-error">
          {error}
        </div>
      ) : null}

      <button
        type="button"
        class="team-door__go"
        disabled={!ready}
        data-testid="add-tech-submit"
        onClick={() => void submit()}
      >
        {busy ? 'Working…' : willInvite ? 'Add and invite' : 'Add to roster'}
      </button>
    </div>
  );
}
